import type { FC } from "react"

import { useNavigate } from "react-router-dom"
import type { UseTableOptions } from "react-table"
import { useFlexLayout, useResizeColumns, useTable } from "react-table"
import type { FlattenSimpleInterpolation } from "styled-components"
import styled from "styled-components"

const Wrapper = styled.div<{ css?: FlattenSimpleInterpolation }>`
  ${({ css }) => css}
`
export type IdentifiedObject = { id?: string }

export type TableProps<MODEL extends IdentifiedObject> =
  UseTableOptions<MODEL> & {
    name: string
    clickable?: boolean
    css?: FlattenSimpleInterpolation
  }

export const FlexTable = <MODEL extends IdentifiedObject>({
  data,
  columns,
  name,
  clickable,
  css,
}: TableProps<MODEL>): ReturnType<FC> => {
  const navigate = useNavigate()
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useResizeColumns, useFlexLayout)

  return (
    <Wrapper css={css}>
      <div className="table" {...getTableProps()}>
        <div className="thead">
          {headerGroups.map((headerGroup) => (
            <div className="tr" key={headerGroup.id}>
              {headerGroup.headers.map((column) => (
                <div key={column.id} className="th">
                  {column.render(`Header`)}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="tbody">
          {rows.map((row: any) => {
            prepareRow(row)

            return (
              // eslint-disable-next-line react/jsx-key
              <div
                {...row.getRowProps()}
                className="tr"
                onClick={
                  clickable
                    ? () => navigate(`/${name}/${row.original.id}`)
                    : undefined
                }
                data-test={`${
                  row?.original?.powurOsId ?? row?.original?.id
                }-table-row`}
                data-testid={`${
                  row?.original?.powurOsId ?? row?.original?.id
                }-table-row`}
              >
                {row.cells.map((cell: any) => {
                  return (
                    // eslint-disable-next-line react/jsx-key
                    <div
                      data-testid={`${cell.value}-cell`}
                      className="td"
                      {...cell.getCellProps()}
                    >
                      {cell.render(`Cell`)}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </Wrapper>
  )
}
